import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, FileText, History, Lock } from "lucide-react";
import { api, fmtError, BACKEND_URL } from "@/lib/api";
import { fmtDate } from "@/constants/labels";
import { AdminArticles } from "@/components/AdminArticles";

export default function AdminCommandesMentions() {
  const [current, setCurrent] = useState({ mentions: "", version: 0, updated_at: null });
  const [draft, setDraft] = useState("");
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [c, h] = await Promise.all([api.get("/commandes/mentions"), api.get("/admin/commandes/mentions/history")]);
    setCurrent(c.data); setDraft(c.data.mentions || ""); setHistory(h.data.versions || []);
  }, []);
  useEffect(() => { load().catch((e) => toast.error(fmtError(e))); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/commandes/mentions", { mentions: draft });
      toast.success(data.unchanged ? "Aucun changement" : `Mentions enregistrées — version ${data.version}`); await load();
    } catch (err) { toast.error(fmtError(err)); } finally { setBusy(false); }
  };
  const dirty = draft !== (current.mentions || "");

  return (
    <div className="max-w-4xl" data-testid="admin-mentions-page">
      <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]">Commande de travaux valant contrat — mentions et articles</h1>
      <p className="mt-2 text-sm text-[#3E4C59] leading-relaxed">
        Texte imprimé dans le bloc « Mentions » de chaque PDF de commande de travaux. Texte simple, sans mise en forme ; les sauts de ligne sont conservés.
      </p>
      <div className="mt-3 card p-3 border-l-4 border-[#1B3A5C] text-sm text-[#1F2933] flex gap-2 items-start" data-testid="admin-mentions-freeze-note">
        <Lock size={16} className="shrink-0 mt-0.5 text-[#1B3A5C]" />
        <span>Une commande <b>acceptée</b> est figée : elle conserve la version des mentions en vigueur au moment de son acceptation et ne suit pas les modifications ultérieures. Seules les commandes acceptées après l'enregistrement utiliseront le nouveau texte.</span>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div>
          <label className="label">Texte des mentions <span className="font-normal text-[#3E4C59]">— version actuelle v{current.version}{current.updated_at ? `, ${fmtDate(current.updated_at)}` : ""}</span></label>
          <textarea className="textarea font-mono text-sm" rows={18} maxLength={8000} value={draft} onChange={(e) => setDraft(e.target.value)}
            placeholder="Aucune mention pour l'instant. Collez ici le texte validé par votre juriste." data-testid="admin-mentions-textarea" />
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-[#94A3B8]">{draft.length}/8000</span>
            <button className="btn-primary" disabled={busy || !dirty} onClick={save} data-testid="admin-mentions-save"><Save size={14} /> Enregistrer une nouvelle version</button>
          </div>
        </div>
        <div>
          <label className="label">Aperçu sur le PDF</label>
          <div className="card p-4 text-sm" data-testid="admin-mentions-preview">
            <div className="font-bold text-[#1F2933]">Mentions</div>
            {draft.trim()
              ? <div className="mt-1 whitespace-pre-wrap text-[#1F2933] leading-relaxed">{draft}</div>
              : <div className="mt-1 text-[#94A3B8] italic">Bloc absent du PDF tant que le texte est vide.</div>}
          </div>
          <a className="btn-outline mt-3 inline-flex" href={`${BACKEND_URL}/api/admin/commandes/mentions/preview.pdf`} target="_blank" rel="noreferrer" data-testid="admin-mentions-preview-pdf">
            <FileText size={14} /> Ouvrir un PDF d'exemple (version enregistrée)
          </a>
        </div>
      </div>

      <h2 className="mt-10 text-lg font-semibold text-[#1F2933] inline-flex items-center gap-2"><History size={18} /> Historique des versions</h2>
      <div className="mt-3 space-y-2" data-testid="admin-mentions-history">
        {history.length === 0 && <div className="card p-4 text-sm text-[#3E4C59]">Aucune version enregistrée.</div>}
        {history.map((v) => (
          <details key={v.version} className="card p-3" data-testid={`admin-mentions-version-${v.version}`}>
            <summary className="cursor-pointer text-sm flex items-center justify-between">
              <span><b>Version {v.version}</b> — {fmtDate(v.at)} <span className="text-[#94A3B8]">par {v.by_email || v.by}</span></span>
              {v.version === current.version && <span className="badge-valid">en vigueur</span>}
            </summary>
            <pre className="mt-3 whitespace-pre-wrap font-mono text-xs text-[#1F2933] bg-[#F5F6F7] p-3 rounded-[4px]">{v.mentions || "(vide)"}</pre>
          </details>
        ))}
      </div>
      <AdminArticles />
    </div>
  );
}
